import { getMimeTypeForExtension } from '~/messages/attachmentUtils'

export type PickerMode = 'camera' | 'gallery' | 'document'

const ANY_IMAGE = 'image/*'
const ANY_FILE = '*/*'

const splitAccept = (accept: string): string[] => (accept || '')
  .split(',')
  .map(token => token.trim().toLowerCase())
  .filter(Boolean)

const isExtensionToken = (token: string): boolean => token.startsWith('.')

const toMimeTypes = (tokens: string[]): string[] => {
  const mimeTypes: string[] = []
  for (const token of tokens) {
    const mime = isExtensionToken(token) ? getMimeTypeForExtension(token) : token
    if (mime && mimeTypes.indexOf(mime) === -1) {
      mimeTypes.push(mime)
    }
  }
  return mimeTypes
}

/**
 * Builds the `accept` value for one picker intent.
 *
 * Camera and gallery always ask for `image/*` so the OS opens the camera resp. the photo
 * gallery instead of a generic file browser; anything the user picks there is still checked
 * against the allowed extensions on upload.
 *
 * The document intent has to be translated per platform:
 * - Android copies every accept token into `Intent.EXTRA_MIME_TYPES`, so extension tokens
 *   (".pdf") open the picker with nothing selectable and must become MIME types.
 * - iOS resolves both extensions and MIME types to UTIs, but does not know every Office
 *   MIME type, so it gets both spellings.
 * - Browsers handle the extension list as authored.
 */
export const resolvePickerAccept = (accept: string, mode: PickerMode, platformId?: string): string => {
  if (mode !== 'document') {
    return ANY_IMAGE
  }

  const tokens = splitAccept(accept)

  if (platformId === 'android') {
    const mimeTypes = toMimeTypes(tokens)
    return mimeTypes.length > 0 ? mimeTypes.join(',') : ANY_FILE
  }

  if (platformId === 'ios') {
    return tokens.filter(isExtensionToken).concat(toMimeTypes(tokens)).join(',')
  }

  return tokens.join(',')
}
